import React, { useEffect, useState } from "react";
import classes from "./ViewUserModal.module.css";
import API from "../../../utils/api";

const permissionLabels = {
  dashboard_quick_action: "Dashboard · Quick Actions",
  dashboard_hide_view_all: "Dashboard · Hide View All",
  dashboard_remove_cards: "Dashboard · Remove Summary Cards",
  dashboard_card_products: "Dashboard · Summary Card (Products)",
  dashboard_card_categories: "Dashboard · Summary Card (Categories)",
  dashboard_card_brands: "Dashboard · Summary Card (Brands)",
  product_add: "Products · Add Page",
  product_view: "Products · View Page",
  product_import: "Products · Import Page",
  product_edit: "Products · Edit Action",
  product_delete: "Products · Delete Action",
  category_add: "Categories · Add Page",
  category_view: "Categories · View Page",
  category_edit: "Categories · Edit Action",
  category_delete: "Categories · Delete Action",
  brand_add: "Brands · Add Page",
  brand_view: "Brands · View Page",
  brand_edit: "Brands · Edit Action",
  brand_delete: "Brands · Delete Action",
  contact_access: "Contact Inbox",
  user_manage: "User Management",
};

const orderedDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export default function ViewUserModal({ user, onClose }) {
  const [loginLogs, setLoginLogs] = useState({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    let active = true;

    const fetchLogs = async () => {
      setLogsLoading(true);
      setLogsError("");
      try {
        const token = localStorage.getItem("authToken") || "";
        const response = await fetch(`${API.base}/users.php`, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({ action: "login_logs", id: user.id }),
        });
        const data = await response.json();
        if (!response.ok || !data.success)
          throw new Error(data.error || "Failed to load activity");
        if (active) setLoginLogs(data.weekly || {});
      } catch (err) {
        if (active) setLogsError(err.message || "Failed to load activity");
      } finally {
        if (active) setLogsLoading(false);
      }
    };

    fetchLogs();
    return () => (active = false);
  }, [user?.id]);

  if (!user) return null;

  const activePermissions = Object.entries(permissionLabels)
    .filter(([key]) => Number(user[key]) === 1)
    .map(([, label]) => label);

  return (
    <div className={classes.backdrop} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <header className={classes.header}>
          <div className={classes.userBlock}>
            <div className={classes.avatar}>
              {user.name?.charAt(0).toUpperCase() ||
                user.email.charAt(0).toUpperCase()}
            </div>
            <div className={classes.nameBlock}>
              <h2 className={classes.name}>{user.name || user.email}</h2>
              <p className={classes.role}>
                {user.role === "admin" ? "Administrator" : "Standard User"}
              </p>
            </div>
          </div>
          <button className={classes.closeBtn} onClick={onClose}>
            ✕
          </button>
        </header>

        {/* BODY */}
        <div className={classes.body}>
          {/* USER INFO */}
          <section className={classes.section}>
            <h3 className={classes.sectionTitle}>User Information</h3>
            <div className={classes.infoGrid}>
              <div>
                <label>Email</label>
                <p>{user.email}</p>
              </div>
              <div>
                <label>Phone</label>
                <p>{user.phone || "—"}</p>
              </div>
              <div>
                <label>Role</label>
                <p>{user.role === "admin" ? "Administrator" : "User"}</p>
              </div>
            </div>
          </section>

          {/* PERMISSIONS */}
          <section className={classes.section}>
            <h3 className={classes.sectionTitle}>Permissions</h3>
            {activePermissions.length === 0 ? (
              <p className={classes.empty}>No permissions assigned.</p>
            ) : (
              <div className={classes.permList}>
                {activePermissions.map((label) => (
                  <span key={label} className={classes.permItem}>
                    {label}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* WEEKLY LOGIN ACTIVITY */}
          <section className={classes.section}>
            <h3 className={classes.sectionTitle}>Weekly Login Activity</h3>
            {logsLoading ? (
              <p className={classes.loading}>Loading activity…</p>
            ) : logsError ? (
              <p className={classes.error}>{logsError}</p>
            ) : (
              <div className={classes.logsGrid}>
                {orderedDays.map((day) => (
                  <div key={day} className={classes.dayColumn}>
                    <p className={classes.day}>{day}</p>
                    <div className={classes.logsScroll}>
                      {(loginLogs[day] || []).length === 0 ? (
                        <p className={classes.noLog}>No Activity</p>
                      ) : (
                        loginLogs[day].map((entry, idx) => (
                          <div key={idx} className={classes.logCard}>
                            <div className={classes.logTime}>{entry.time}</div>
                            <div className={classes.logDot}></div>
                            <div className={classes.logDate}>{entry.date}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
